import "@ids/style-button";
import "@ids/style-css-helpers";
import "@ids/style-dropdown-list";
import "@ids/style-dropdown-select";
import "@ids/style-error-message";
import "@ids/style-input-field";
import "@ids/style-label";
import "@ids/style-loader";
import "@ids/style-modal-v19";
import "@ids/style-scaffolding";
import type { ComponentType } from "react";
import { createElement } from "react";
import { ProductType } from "@/models/ProductType";
import { getWebComponentLanguage } from "./hooks/locationLanguage";
import { injectGlobalStyles } from "./injectGlobalStyles";
import { prefetchEntryContent } from "./prefetchEntryContent";
import ProductShadowBase from "./productShadowBase";

injectGlobalStyles();

type EntryProps = Record<string, never>;

type EntryModule = {
  default: ComponentType<EntryProps>;
};

type EntryTagName =
  | "travel-entry"
  | "householder-entry"
  | "baby-entry"
  | "child-entry"
  | "dog-entry"
  | "cat-entry"
  | "van-entry"
  | "car-entry"
  | "mc-entry"
  | "landlord-entry"
  | "camper-entry"
  | "moped-entry"
  | "sport-entry"
  | "income-entry";

const entryProductByTagName: Record<EntryTagName, ProductType> = {
  "travel-entry": ProductType.TRAVEL,
  "householder-entry": ProductType.HOUSEHOLDER,
  "baby-entry": ProductType.BABY,
  "child-entry": ProductType.CHILD,
  "dog-entry": ProductType.DOG,
  "cat-entry": ProductType.CAT,
  "van-entry": ProductType.VAN,
  "car-entry": ProductType.CAR,
  "mc-entry": ProductType.MC,
  "landlord-entry": ProductType.LANDLORD,
  "camper-entry": ProductType.CAMPER,
  "moped-entry": ProductType.MOPED,
  "sport-entry": ProductType.SPORT,
  "income-entry": ProductType.INCOME,
};

function defineLazyEntryElement(
  tagName: EntryTagName,
  // Path of the dynamically-imported entry module, relative to
  // src/web_components/ and without extension (e.g. "travel/NewTravelEntryForm").
  // This MUST match the module specifier passed to `importEntry`'s import()
  // call below it, since it's used to derive the manifest key for CSS lookup
  // (see manifestEntrySource in productShadowBase.tsx). Keeping both values
  // next to each other at the call site (instead of an unrelated literal)
  // prevents them from drifting apart.
  modulePath: string,
  importEntry: () => Promise<EntryModule>,
) {
  class LazyEntryElement extends ProductShadowBase {
    connectedCallback() {
      this.manifestEntrySource = `src/web_components/${modulePath}.tsx`;
      this.primeResourceLoading();
      const language = getWebComponentLanguage(this.language ?? undefined);
      const product = entryProductByTagName[tagName];
      prefetchEntryContent({
        product,
        language,
        backendHost: this.backendHost ?? undefined,
      });

      void importEntry().then(({ default: Entry }) => {
        this.loadAndRender(() => createElement(Entry));
      });
    }
  }

  customElements.define(tagName, LazyEntryElement);
}
defineLazyEntryElement(
  "travel-entry",
  "travel/NewTravelEntryForm",
  () => import("./travel/NewTravelEntryForm"),
);
defineLazyEntryElement(
  "householder-entry",
  "householder/NewHouseholderEntryForm",
  () => import("./householder/NewHouseholderEntryForm"),
);
defineLazyEntryElement(
  "baby-entry",
  "baby/NewBabyEntryForm",
  () => import("./baby/NewBabyEntryForm"),
);
defineLazyEntryElement(
  "child-entry",
  "child/NewChildEntryForm",
  () => import("./child/NewChildEntryForm"),
);
defineLazyEntryElement(
  "dog-entry",
  "dog/NewDogEntryForm",
  () => import("./dog/NewDogEntryForm"),
);
defineLazyEntryElement(
  "cat-entry",
  "cat/NewCatEntryForm",
  () => import("./cat/NewCatEntryForm"),
);
defineLazyEntryElement(
  "van-entry",
  "van/NewVanEntryForm",
  () => import("./van/NewVanEntryForm"),
);
defineLazyEntryElement(
  "car-entry",
  "car/NewCarEntryForm",
  () => import("./car/NewCarEntryForm"),
);
defineLazyEntryElement(
  "mc-entry",
  "mc/NewMcEntryForm",
  () => import("./mc/NewMcEntryForm"),
);
defineLazyEntryElement(
  "landlord-entry",
  "landlord/NewLandlordEntryForm",
  () => import("./landlord/NewLandlordEntryForm"),
);
defineLazyEntryElement(
  "camper-entry",
  "camper/NewCamperEntryForm",
  () => import("./camper/NewCamperEntryForm"),
);
defineLazyEntryElement(
  "moped-entry",
  "moped/MopedEntryForm",
  () => import("./moped/MopedEntryForm"),
);
defineLazyEntryElement(
  "sport-entry",
  "sport/NewSportEntryForm",
  () => import("./sport/NewSportEntryForm"),
);
defineLazyEntryElement(
  "income-entry",
  "income/NewIncomeEntryForm",
  () => import("./income/NewIncomeEntryForm"),
);
